/**
 * Өдрийн гутлын урсгал — шинэ гутал (аяллаар), шилжүүлэг, буцаалт,
 * актлалт, баталгааны нэхэмжлэл. Хоосон хэсэг гарахгүй.
 *
 * ⚠️ «Агуулахад орсон» нь шилжүүлгийг ОРУУЛАХГҮЙ — салбараас буцаж
 *    ирсэн гутал доорх «Шилжүүлэг» хэсэгт харагдана
 *    (`@boots/domain` → `calendar.ts`).
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  formatMnt, formatQty, formatSerialDisplay, type CalendarDayReport, type CalendarTripIntake,
} from '@boots/domain';
import { useAuth } from '../../lib/auth';
import { CLAIM_RESOLUTION_LABEL, PAYMENT_LABEL, RETURN_DISPOSITION_LABEL } from '../../lib/types';
import { RETURN_TYPE_LABEL } from '../../lib/refunds';
import { DayBlock } from './day-block';
import { DataTable, type Column } from '../data-table';

const LINK = 'text-accent-700 hover:underline';

export function DayStock({ r }: { r: CalendarDayReport }) {
  const { hasPerm } = useAuth();
  const orderLink = (id: string, code: string) =>
    hasPerm('orders.view') ? <Link to={`/orders?open=${id}`} className={`num ${LINK}`}>{code}</Link> : code;
  // Шошго олгохоос өмнө актлагдсан гутал серийн дугааргүй — «—» холбоос болж харагдахгүй
  const itemLink = (id: string, serial: string | null) => {
    const text = serial ? formatSerialDisplay(serial) : 'Шошгогүй гутал';
    return hasPerm('items.view') ? <Link to={`/items/${id}`} className={`num ${LINK}`}>{text}</Link> : text;
  };

  return (
    <>
      {r.intake.trips.length > 0 ? (
        <DayBlock title="Шинэ гутал" count={r.intake.trips.length}>
          <TripsTable trips={r.intake.trips} canOpen={hasPerm('purchasing.view')} />
          <p className="text-label text-ink-3">
            Бүртгэгдсэн — системд шинээр үүссэн. Агуулахад орсон — Оросоос ирж агуулахад хүлээн
            авсан; салбараас буцаж ирсэн шилжүүлэг орохгүй.
          </p>
        </DayBlock>
      ) : null}

      {r.transfers.length > 0 ? (
        <DayBlock title="Шилжүүлэг" count={r.transfers.length}>
          <ul>
            {r.transfers.map((t) => (
              <Entry
                key={t.id}
                main={<span className="num">{t.code}</span>}
                meta={`${t.fromStore} → ${t.toStore}`}
                value={[
                  t.sentToday ? `Илгээсэн ${formatQty(t.sentCount)} ш` : '',
                  t.receivedToday
                    ? `Хүлээн авсан ${formatQty(t.receivedCount)}${
                      t.receivedCount < t.sentCount ? ` / ${formatQty(t.sentCount)}` : ''
                    } ш`
                    : '',
                ].filter(Boolean).join(' · ')}
              />
            ))}
          </ul>
        </DayBlock>
      ) : null}

      {r.returns.length > 0 ? (
        <DayBlock title="Буцаалт" count={r.returns.length}>
          <ul>
            {r.returns.map((x) => (
              <Entry
                key={x.id}
                main={orderLink(x.orderId, x.orderCode)}
                meta={[
                  `${RETURN_TYPE_LABEL[x.type]}${x.exchangeOrderCode ? ` → ${x.exchangeOrderCode}` : ''}`,
                  formatSerialDisplay(x.serial),
                  x.storeName,
                  RETURN_DISPOSITION_LABEL[x.disposition] ?? x.disposition,
                  x.refundMethod ? (PAYMENT_LABEL[x.refundMethod] ?? x.refundMethod) : null,
                ].filter(Boolean).join(' · ')}
                value={Number(x.refundMnt) > 0 ? `-${formatMnt(x.refundMnt)}` : '—'}
                alert
              />
            ))}
          </ul>
        </DayBlock>
      ) : null}

      {r.writeOffs.length > 0 ? (
        <DayBlock title="Актлалт" count={r.writeOffs.length}>
          <ul>
            {r.writeOffs.map((w) => (
              <Entry key={w.id} main={itemLink(w.itemId, w.serial)} meta={w.reason} />
            ))}
          </ul>
        </DayBlock>
      ) : null}

      {r.claims.length > 0 ? (
        <DayBlock title="Баталгааны нэхэмжлэл" count={r.claims.length}>
          <ul>
            {r.claims.map((c) => (
              <Entry
                key={c.id}
                main={<span className="num">{c.code}</span>}
                meta={formatSerialDisplay(c.serial)}
                value={CLAIM_RESOLUTION_LABEL[c.resolution] ?? c.resolution}
              />
            ))}
          </ul>
        </DayBlock>
      ) : null}
    </>
  );
}

function Entry({ main, meta, value, alert = false }: {
  main: ReactNode;
  meta?: string;
  value?: string;
  alert?: boolean;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-hair/70 py-2 text-body last:border-0">
      <span className="min-w-0">{main}</span>
      {meta ? <span className="text-meta text-ink-2">{meta}</span> : null}
      {value ? (
        <span className={`num ml-auto whitespace-nowrap ${alert ? 'text-alert-ink' : ''}`}>{value}</span>
      ) : null}
    </li>
  );
}

function TripsTable({ trips, canOpen }: { trips: CalendarTripIntake[]; canOpen: boolean }) {
  const count = (n: number) => (n > 0 ? `${formatQty(n)} ш` : '—');
  const columns: Column<CalendarTripIntake>[] = [
    {
      key: 'trip',
      header: 'Аялал',
      mobile: 'title',
      nowrap: true,
      cell: (t) => (
        <span className="inline-flex items-center gap-1.5">
          {canOpen && t.code ? (
            <Link to={`/purchasing/${t.id}`} className={`num ${LINK}`}>{t.code}</Link>
          ) : (
            <span className="num">{t.code ?? '—'}</span>
          )}
          {t.opening ? (
            <span className="rounded-pill bg-neutral-bg px-2 text-micro text-ink-2">Эхний үлдэгдэл</span>
          ) : null}
        </span>
      ),
    },
    { key: 'supplier', header: 'Нийлүүлэгч', cell: (t) => t.supplierName },
    { key: 'registered', header: 'Бүртгэгдсэн', align: 'right', cell: (t) => count(t.registered) },
    { key: 'warehouse', header: 'Агуулахад орсон', align: 'right', mobile: 'value', cell: (t) => count(t.warehouseIn) },
  ];
  return <DataTable rows={trips} columns={columns} rowKey={(t) => t.id} />;
}
